import { Step } from 'react-joyride';

const getServicesTourSteps = (isMobile: boolean): Step[] => {
  const suffix = isMobile ? '-mobile' : '-desktop';

  const allSteps: (Step & { desktopOnly?: boolean })[] = [
    // ── 1. Título ────────────────────────────────────────────────────────────
    {
      target: '.tour-title',
      content: (
        <div style={{ textAlign: 'justify' }}>
          Bienvenido a <strong>Servicios</strong>. Aquí gestionas todo el
          catálogo de servicios que tu empresa ofrece. Una configuración
          correcta permite que los clientes reserven online y que los
          trabajadores sepan exactamente qué deben hacer y cuánto tiempo
          necesitan.
        </div>
      ),
      placement: 'bottom',
      disableBeacon: true,
    },

    // ── 2. Buscador ──────────────────────────────────────────────────────────
    {
      target: '.tour-filter-input',
      content: (
        <div style={{ textAlign: 'justify' }}>
          Escribe aquí para <strong>filtrar servicios al momento</strong>.
          Puedes buscar por nombre, descripción, precio o duración. Ideal
          cuando el catálogo crece y necesitas localizar un servicio rápido.
        </div>
      ),
      placement: 'bottom',
      disableBeacon: true,
    },

    // ── 3. Botón Categorías ──────────────────────────────────────────────────
    {
      target: '.tour-categories-btn',
      content: (
        <div style={{ textAlign: 'justify' }}>
          Pulsa <strong>Categorías</strong> para organizar tus servicios en
          grupos temáticos. Por ejemplo: <em>Corte</em>, <em>Color</em>,{' '}
          <em>Tratamientos</em>…
          <br />
          <br />
          Desde este panel puedes:
          <ul style={{ margin: '8px 0 0 16px' }}>
            <li>
              <strong>Crear</strong> nuevas categorías con nombre, icono e
              imagen
            </li>
            <li>
              <strong>Editar</strong> o eliminar las existentes
            </li>
            <li>Cambiar su imagen representativa</li>
          </ul>
          <br />
          Las categorías aparecen como <strong>cabeceras en la tabla</strong>{' '}
          y agrupan visualmente los servicios, haciendo mucho más fácil la
          navegación cuando tienes un catálogo amplio.
        </div>
      ),
      placement: 'left',
      disableBeacon: true,
    },

    // ── 4. Botón Añadir servicio ─────────────────────────────────────────────
    {
      target: '.tour-add-btn',
      content: (
        <div style={{ textAlign: 'justify' }}>
          Pulsa <strong>Añadir servicio</strong> para crear uno nuevo. Los
          campos básicos son:
          <ul style={{ margin: '8px 0 0 16px' }}>
            <li>
              <strong>Nombre</strong> del servicio
            </li>
            <li>
              <strong>Categoría</strong> — para agruparlo en la tabla
            </li>
            <li>
              <strong>Icono</strong> — aparece en listas y en la página de
              reservas
            </li>
            <li>
              <strong>Descripción</strong> — puedes generarla automáticamente
              con IA ✨
            </li>
            <li>
              <strong>Precio</strong> y <strong>duración</strong> en minutos
            </li>
          </ul>
          <br />
          En <strong>configuración avanzada</strong> también puedes:
          <ul style={{ margin: '8px 0 0 16px' }}>
            <li>Controlar mínimo y máximo de clientes por reserva</li>
            <li>Elegir si el precio aplica por persona o por servicio</li>
            <li>Activar reservas en grupo y requerir trabajador</li>
            <li>Limitar el servicio a días concretos de la semana</li>
            <li>Definir franjas horarias específicas</li>
          </ul>
          <br />
          Si marcas <strong>"Requiere trabajador"</strong>, al guardar se
          abrirá directamente un panel para asignar los trabajadores que lo
          realizan.
        </div>
      ),
      placement: 'left',
      disableBeacon: true,
    },

    // ── 5. Hint de arrastre (solo escritorio) ────────────────────────────────
    {
      target: '.tour-drag-hint',
      content: (
        <div style={{ textAlign: 'justify' }}>
          Cuando tienes servicios en varias categorías, puedes{' '}
          <strong>reasignarlos arrastrando</strong> sin necesidad de editar el
          servicio.
          <br />
          <br />
          Coge el icono <strong>⠿</strong> de cualquier fila y suéltalo sobre
          la cabecera de la categoría destino. Esta se resaltará en azul y
          mostrará el texto <em>"Soltar aquí →"</em> para confirmarte que
          puedes soltar.
        </div>
      ),
      placement: 'bottom',
      disableBeacon: true,
      desktopOnly: true,
    },

    // ── 6. Cabecera de categoría (ambas plataformas) ─────────────────────────
    {
      target: '.tour-category-header',
      content: (
        <div style={{ textAlign: 'justify' }}>
          Esta es la <strong>cabecera de una categoría</strong>. Muestra la
          imagen, el nombre y el número de servicios que contiene.
          <br />
          <br />
          <ul style={{ margin: '8px 0 0 16px' }}>
            <li>
              Pulsa el <strong>nombre o la flecha</strong> para colapsar o
              expandir el grupo y ahorrar espacio en pantalla
            </li>
            <li>
              Pulsa el <strong>lápiz ✏️</strong> junto al nombre para editar
              la categoría (nombre, icono e imagen)
            </li>
          </ul>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: true,
    },

    // ── 7. Soltar servicio al arrastrar (solo escritorio) ────────────────────
    {
      target: '.tour-group-header',
      content: (
        <div style={{ textAlign: 'justify' }}>
          <strong>Suelta un servicio aquí</strong> mientras arrastras para
          moverlo a esta categoría. Cuando pasa por encima, la cabecera se
          ilumina en azul para indicarte que puedes soltar.
        </div>
      ),
      placement: 'bottom',
      disableBeacon: true,
      desktopOnly: true,
    },

    // ── 8. Icono de arrastre (solo escritorio) ───────────────────────────────
    {
      target: '.tour-drag-handle',
      content: (
        <div style={{ textAlign: 'justify' }}>
          Este es el <strong>icono de puntos</strong>, para mover el servicio
          arrastrándolo. Haz clic sobre él y arrastra para cambiarlo de
          categoría sin necesidad de editarlo.
          <br />
          <br />
          Mientras arrastras verás una <strong>tarjeta flotante</strong> con
          el nombre y el precio del servicio, y la categoría destino se
          resaltará en azul al pasar por encima.
        </div>
      ),
      placement: 'right',
      disableBeacon: true,
      desktopOnly: true,
    },

    // ── 9. Tabla (solo escritorio) ────────────────────────────────────────────
    {
      target: '.tour-table-head',
      content: (
        <div style={{ textAlign: 'justify' }}>
          La <strong>tabla de servicios</strong> muestra de un vistazo toda la
          información relevante del catálogo. Haz clic en cualquier cabecera
          de columna para <strong>ordenar</strong> la lista de forma
          ascendente o descendente.
          <br />
          <br />
          Columnas disponibles:
          <ul style={{ margin: '8px 0 0 16px' }}>
            <li>
              <strong>Servicio</strong> — nombre e icono
            </li>
            <li>
              <strong>Descripción</strong>
            </li>
            <li>
              <strong>Precio</strong>
            </li>
            <li>
              <strong>Duración</strong>
            </li>
            <li>
              <strong>Público</strong> — si aparece en la página de reservas
              online
            </li>
            <li>
              <strong>Fases</strong> — si tiene pasos configurados
            </li>
            <li>
              <strong>Activo</strong> — si está disponible para reservar
            </li>
          </ul>
        </div>
      ),
      placement: 'top',
      disableBeacon: true,
      desktopOnly: true,
    },

    // ── 10. Badge Público (solo escritorio) ──────────────────────────────────
    {
      target: '.tour-publico-switch',
      content: (
        <div style={{ textAlign: 'justify' }}>
          <strong>Servicio público</strong> controla si el servicio aparece
          en el perfil público de tu empresa y en la página de reservas
          online.
          <br />
          <br />
          <span style={{ color: '#10B981', fontWeight: 'bold' }}>✔ Sí</span>{' '}
          — cualquier cliente puede verlo y reservarlo.
          <br />
          <span style={{ color: '#EF4444', fontWeight: 'bold' }}>✘ No</span>{' '}
          — solo disponible para gestión interna desde el panel de
          administración.
          <br />
          <br />
          Puedes cambiar este valor en cualquier momento editando el
          servicio con el lápiz ✏️.
        </div>
      ),
      placement: 'right',
      disableBeacon: true,
      desktopOnly: true,
    },

    // ── 11. Badge Fases (solo escritorio) ────────────────────────────────────
    {
      target: '.tour-fases-badge',
      content: (
        <div style={{ textAlign: 'justify' }}>
          <strong>Fases</strong> indica si el servicio está dividido en pasos
          (por ejemplo: aplicar, esperar, lavar).
          <br />
          <br />
          <span style={{ color: '#10B981', fontWeight: 'bold' }}>✔ Sí</span>{' '}
          — tiene fases configuradas, cada una con su duración y si requiere
          atención del trabajador.
          <br />
          <span style={{ color: '#EF4444', fontWeight: 'bold' }}>✘ No</span>{' '}
          — el servicio se realiza de un tirón, sin pasos intermedios.
          <br />
          <br />
          Configúralas desde el lápiz ✏️ de edición.
        </div>
      ),
      placement: 'right',
      disableBeacon: true,
      desktopOnly: true,
    },

    // ── 12. Badge Activo (solo escritorio) ───────────────────────────────────
    {
      target: '.tour-activo-badge',
      content: (
        <div style={{ textAlign: 'justify' }}>
          <strong>Activo</strong> controla si el servicio está disponible para
          reservar ahora mismo.
          <br />
          <br />
          <span style={{ color: '#10B981', fontWeight: 'bold' }}>✔ Sí</span>{' '}
          — los clientes y trabajadores pueden reservarlo con normalidad.
          <br />
          <span style={{ color: '#EF4444', fontWeight: 'bold' }}>✘ No</span>{' '}
          — queda pausado: no se puede reservar, pero conserva su
          configuración e historial. Útil para servicios de temporada o
          temporalmente fuera de catálogo.
        </div>
      ),
      placement: 'right',
      disableBeacon: true,
      desktopOnly: true,
    },

    // ── 13. Botón ver detalles ────────────────────────────────────────────────
    {
      target: `.tour-details-btn${suffix}`,
      content: (
        <div style={{ textAlign: 'justify' }}>
          Pulsa aquí para <strong>ver los detalles completos</strong> del
          servicio: toda su configuración avanzada, fases (si las tiene),
          días y franjas horarias en las que está disponible, en una sola
          pantalla de solo lectura.
        </div>
      ),
      placement: 'left',
      disableBeacon: true,
    },

    // ── 12. Botón editar ──────────────────────────────────────────────────────
    {
      target: `.tour-edit-btn${suffix}`,
      content: (
        <div style={{ textAlign: 'justify' }}>
          Pulsa el <strong>lápiz ✏️</strong> para editar un servicio. Puedes
          modificar cualquier campo: nombre, categoría, icono, descripción,
          precio, duración y toda la configuración avanzada.
          <br />
          <br />
          También puedes acceder a las <strong>fases del servicio</strong>{' '}
          para dividirlo en pasos con su duración y si requieren atención del
          trabajador.
          <br />
          <br />
          <strong>Ejemplo de fases para "Tinte de pelo":</strong>
          <ul style={{ margin: '8px 0 0 16px' }}>
            <li>Aplicar tinte — 15 min, requiere atención ✔️</li>
            <li>Esperar efecto — 30 min, sin atención ✘</li>
            <li>Lavar y secar — 20 min, requiere atención ✔️</li>
          </ul>
          <br />
          Con fases, el trabajador puede atender a otro cliente durante el
          tiempo de espera, optimizando su agenda.
        </div>
      ),
      placement: 'left',
      disableBeacon: true,
    },

    // ── 13. Botón eliminar ────────────────────────────────────────────────────
    {
      target: `.tour-delete-btn${suffix}`,
      content: (
        <div style={{ textAlign: 'justify' }}>
          Pulsa la <strong>papelera 🗑️</strong> para eliminar un servicio. Te
          pedirá confirmación antes de borrarlo, y se eliminarán también sus
          fases, horarios, días y asignaciones de trabajadores.
          <br />
          <br />
          Si el servicio <strong>tiene citas asociadas</strong>, el botón
          aparecerá deshabilitado — primero tendrás que resolver esas citas
          antes de poder eliminarlo.
        </div>
      ),
      placement: 'left',
      disableBeacon: true,
    },

    // ── 14. Paginación ────────────────────────────────────────────────────────
    {
      target: '.tour-pagination',
      content: (
        <div style={{ textAlign: 'justify' }}>
          Si tienes muchos servicios usa la <strong>paginación</strong> para
          navegar entre páginas. También puedes cambiar cuántos registros se
          muestran a la vez (5, 10, 20 o 50) — tu preferencia se guarda
          automáticamente para la próxima visita.
        </div>
      ),
      placement: 'top',
      disableBeacon: true,
    },
  ];

  return allSteps
    .filter((s) => !(isMobile && s.desktopOnly))
    .map(({ desktopOnly, ...step }) => step);
};

export default getServicesTourSteps;